import React from 'react'
import { Teacher } from '../../providers/context'
import { makeRequest, showAxiosErrorAsToast } from '../../request'
import { Divider } from '../../components/simple/divider'
import { Button } from '../../components/simple/button'
import { IconEdit, IconSignIn, IconTimes, IconTrash, IconTrashRestore } from '../../icons/library'
import { formatPhoneNumberForEditing } from '../../utils'
import { RemoteHTMLTooltip, Tooltip } from '../../components/simple/tooltip'
import { Badge, BadgeStyle } from '../../components/simple/badge'
import { EditResponse, LanguageEdit, showEditErrors } from '../modals/editUser'
import { Popups } from '../../providers/popups'
import { EmailEdit, EmailRow, GenericContact, LanguageRow, PhoneEdit, PhoneRow, UsageRow } from './contactComponents'
import { StudentContact } from './familyContactData'
import { getComposeLink, getDirectLink, URLAction } from '../../providers/URLParams'
import { useLanguages } from '../../providers/languages'
import { Link } from 'react-router-dom'
import { SmartLink } from '../../components/simple/link'
import { TextInput } from '../../components/form/textInput'
import { IconCircle } from '../../icons/library'

export const ContactStudent: React.FC<{
  student: StudentContact
  reloadContacts: () => void
  user: Teacher
}> = ({ student, user, reloadContacts }) => {
  const languages = useLanguages()

  const [editing, setEditing] = React.useState(false)
  const [loading, setLoading] = React.useState(false)

  const [language, setLanguage] = React.useState(student.language)
  const [spokenLanguage, setSpokenLanguage] = React.useState(student.spokenLanguage)
  const [receiveEmails, setReceiveEmails] = React.useState(student.emailData.receiveEmails)
  const [email, setEmail] = React.useState(student.emailData.email)
  const [emailInvalid, setEmailInvalid] = React.useState(false)
  const [phoneNumber, setPhoneNumber] = React.useState(formatPhoneNumberForEditing(student.phoneData.phoneNumber))
  const [phoneNumberInvalid, setPhoneNumberInvalid] = React.useState(false)
  const [receiveTexts, setReceiveTexts] = React.useState(student.phoneData.receiveTexts)
  const [textAsWhatsApp, setTextAsWhatsApp] = React.useState(
    student.phoneData.textAsWhatsApp && student.phoneData.receiveTexts
  )
  const [sendTextToSpeech, setSendTextToSpeech] = React.useState(student.phoneData.sendTextToSpeech)
  const [receiveCalls, setReceiveCalls] = React.useState(student.phoneData.receiveCalls)
  const [preferredName, setPreferredName] = React.useState(student.preferredName || '')
  const resetEdit = () => {
    setLanguage(student.language)
    setSpokenLanguage(student.spokenLanguage)
    setReceiveEmails(student.emailData.receiveEmails)
    setEmail(student.emailData.email)
    setEmailInvalid(false)
    setPhoneNumber(formatPhoneNumberForEditing(student.phoneData.phoneNumber))
    setPhoneNumberInvalid(false)
    setReceiveTexts(student.phoneData.receiveTexts)
    setTextAsWhatsApp(student.phoneData.textAsWhatsApp)
    setSendTextToSpeech(student.phoneData.sendTextToSpeech)
    setReceiveCalls(student.phoneData.receiveCalls)
    setPreferredName(student.preferredName || '')
  }
  React.useEffect(resetEdit, [student])

  const edit = () => {
    resetEdit()
    setEditing(true)
  }
  const saveEdit = async () => {
    setLoading(true)
    setEmailInvalid(false)
    setPhoneNumberInvalid(false)
    try {
      const response = await makeRequest<EditResponse>('/frontend_api/editUser', {
        id: student.id,
        language: student.languageEditable ? language : undefined,
        spokenLanguage: spokenLanguage || '',
        email: student.emailData.showEditSection && student.emailData.canEditEmail ? email : undefined,
        receiveEmails: student.emailData.sendingManuallyDeactivated && user.admin === 0 ? undefined : receiveEmails,
        phoneNumber:
          student.phoneData.showEditSection && student.phoneData.canEditPhoneNumber ? phoneNumber : undefined,
        receiveTexts: student.phoneData.sendingManuallyDeactivated && user.admin === 0 ? undefined : receiveTexts,
        receiveTextsAsWhatsapp: textAsWhatsApp,
        audioOnly: sendTextToSpeech,
        receiveCalls: receiveCalls,
        preferredName: user.admin > 0 ? preferredName : undefined,
      })
      if (response === 'success') {
        reloadContacts()
        // loading is reset when the reload completes
      } else showEditErrors(response, setPhoneNumberInvalid, setEmailInvalid)
    } catch (e) {
      showAxiosErrorAsToast(e)
    } finally {
      setLoading(false)
    }
  }
  const toggleActive = async (active: boolean) => {
    if (active || (await Popups.simpleConfirm('Are you sure you want to delete this student'))) {
      setLoading(true)
      makeRequest('/frontend_api/editUser', {
        id: student.id,
        active,
      })
        .then(reloadContacts)
        .catch(showAxiosErrorAsToast)
        .finally(() => setLoading(false))
    }
  }

  return (
    <GenericContact
      avatar={student.avatar}
      deleted={student.deleted}
      loading={loading}
      title={
        <>
          <div className="mr-auto flex flex-col pr-2 leading-5">
            <Link
              className="text-[15px] font-bold no-underline hover:underline"
              to={getDirectLink({
                [URLAction.OpenInboxTab]: 'sent',
                [URLAction.SetSearchUser]: { id: student.id, name: student.name },
                [URLAction.SetAllSchoolMessages]: user.admin > 0,
              })}
            >
              {student.name}
              {student.preferredName && (
                <IconCircle
                  className="ml-1 text-warning"
                  size="xs"
                  tooltip={`Name from SIS: ${student.originalName}`}
                />
              )}
            </Link>
            {student.schools.length > 0 ? (
              <Tooltip text={student.schools.length === 1 ? undefined : student.schools.join(', ')}>
                <span className="text-nowrap text-sm font-bold text-slate-600">
                  {student.schools.length === 1 ? student.schools[0] : `${student.schools.length} schools`}
                </span>
              </Tooltip>
            ) : (
              <span className="text-sm font-bold text-red-500">No School</span>
            )}
          </div>
          {!editing && student.authUserCanSignInAs && (
            <SmartLink to={`/admin/login/${student.id}`}>
              <IconSignIn className="text-charcoal-text" tooltip={`Login as ${student.name}`} />
            </SmartLink>
          )}
          {editing ? (
            <>
              <Button logo small onClick={saveEdit}>
                Save
              </Button>
              <IconTimes
                tooltip="Cancel Editing"
                className="cursor-pointer opacity-50"
                onClick={() => setEditing(false)}
              />
            </>
          ) : student.editable ? (
            <IconEdit className="cursor-pointer text-charcoal-text" tooltip="Edit Student" onClick={edit} />
          ) : null}
          {!editing &&
            student.deletable &&
            (student.deleted ? (
              <IconTrashRestore
                className="cursor-pointer text-charcoal-text"
                tooltip="Restore Student"
                onClick={() => toggleActive(true)}
              />
            ) : (
              <IconTrash
                className="cursor-pointer text-charcoal-text"
                tooltip="Delete Student"
                onClick={() => toggleActive(false)}
              />
            ))}
        </>
      }
    >
      {!editing ? (
        <div className="flex items-center gap-2">
          <LanguageRow language={language} spokenLanguage={spokenLanguage} languageGlobe={student.languageGlobe} />
          <Badge className="ml-auto" badgeStyle={BadgeStyle.mint} tooltip={`Grade ${student.grade}`}>
            {student.grade || 'none'}
          </Badge>
          {student.realClassesCount > 0 && (
            <RemoteHTMLTooltip
              loadFrom={{
                url: '/classInfo',
                data: {
                  student: student.id,
                },
              }}
            >
              <Badge badgeStyle={BadgeStyle.light}>{student.realClassesCount}</Badge>
            </RemoteHTMLTooltip>
          )}
        </div>
      ) : (
        <LanguageEdit
          language={language}
          setLanguage={setLanguage}
          spokenLanguage={spokenLanguage}
          setSpokenLanguage={setSpokenLanguage}
          canEditLanguage={student.languageEditable}
        />
      )}
      <Divider className="-mx-3 !w-auto" />
      {editing ? (
        <EmailEdit
          emailData={student.emailData}
          user={user}
          email={email}
          invalid={emailInvalid}
          setEmail={setEmail}
          receiveEmails={receiveEmails}
          setReceiveEmails={setReceiveEmails}
        />
      ) : (
        <EmailRow
          contactName={student.name}
          contactID={student.id}
          emailData={student.emailData}
          sendEmailMessageLink={getComposeLink({ students: student.id, type: 'email' })}
          setLoading={setLoading}
          reloadContacts={reloadContacts}
        />
      )}
      {editing ? (
        <PhoneEdit
          phoneData={student.phoneData}
          user={user}
          phoneNumber={phoneNumber}
          invalid={phoneNumberInvalid}
          setPhoneNumber={setPhoneNumber}
          receiveTexts={receiveTexts}
          setReceiveTexts={setReceiveTexts}
          textAsWhatsApp={textAsWhatsApp}
          setTextAsWhatsApp={setTextAsWhatsApp}
          showSendTextToSpeech={
            languages.find(language)?.canTextToSpeech || languages.find(spokenLanguage)?.canTextToSpeech || false
          }
          sendTextToSpeech={sendTextToSpeech}
          setSendTextToSpeech={setSendTextToSpeech}
          receiveCalls={receiveCalls}
          setReceiveCalls={setReceiveCalls}
        />
      ) : (
        <PhoneRow
          contactName={student.name}
          contactID={student.id}
          phoneData={student.phoneData}
          sendTextMessageLink={getComposeLink({ students: student.id, type: 'text' })}
          setLoading={setLoading}
          reloadContacts={reloadContacts}
        />
      )}
      {editing && user.admin > 0 && (
        <div className="flex flex-col gap-1">
          <div className="text-sm text-slate-400">Preferred Name</div>
          <TextInput value={preferredName} onChange={setPreferredName} blue />
        </div>
      )}
      <UsageRow
        showText
        data={student.usage}
        deliveryStatusID={user.systemAdministrator ? student.id : undefined}
        className="-mb-1 mt-auto"
      />
    </GenericContact>
  )
}
